import { useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Icon } from '@shared/icons';
import { useListingCountries, useProductMarketplaces } from '@features/products';

export interface SalesScope {
  marketplaces: string[];
  countries: string[];
}

interface SalesScopeFiltersProps {
  value: SalesScope;
  onChange: (value: SalesScope) => void;
}

interface ScopeOption {
  id: string;
  label: string;
  secondary?: string;
}

const SalesScopeFilters = ({ value, onChange }: SalesScopeFiltersProps) => {
  const { marketplaces } = useProductMarketplaces();
  const { countries } = useListingCountries();
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<HTMLElement | null>(null);

  const countryOptions = useMemo<ScopeOption[]>(() => {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return countries
      .map((country) => ({
        id: country.countryCode,
        label: displayNames.of(country.countryCode) ?? country.countryCode,
        secondary: country.countryCode,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, 'en'));
  }, [countries]);

  const marketplaceOptions = useMemo<ScopeOption[]>(
    () => marketplaces.map((marketplace) => ({ id: marketplace.id, label: marketplace.name })),
    [marketplaces],
  );

  if (marketplaceOptions.length === 0 && countryOptions.length === 0) {
    return null;
  }

  const toggleMarketplace = (marketplaceId: string) => {
    const selected = value.marketplaces.includes(marketplaceId)
      ? value.marketplaces.filter((id) => id !== marketplaceId)
      : [...value.marketplaces, marketplaceId];
    onChange({ ...value, marketplaces: selected });
  };

  const toggleCountry = (countryCode: string) => {
    const selected = value.countries.includes(countryCode)
      ? value.countries.filter((code) => code !== countryCode)
      : [...value.countries, countryCode];
    onChange({ ...value, countries: selected });
  };

  const countryLabel = (() => {
    if (value.countries.length === 0) return 'All countries';
    if (value.countries.length === 1) {
      return countryOptions.find((option) => option.id === value.countries[0])?.label ?? value.countries[0];
    }
    return `${value.countries.length} countries`;
  })();

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
    >
      {marketplaceOptions.length > 0 && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: '8px' }}>
          {marketplaceOptions.map((marketplace) => {
            const selected = value.marketplaces.includes(marketplace.id);
            return (
              <Chip key={marketplace.id} size="small" label={<Typography variant="caption">{marketplace.label}</Typography>} onClick={() => toggleMarketplace(marketplace.id)} color={selected ? 'primary' : 'info'} />
            );
          })}
        </Stack>
      )}

      {countryOptions.length > 0 && (
        <Button onClick={(event) => setCountryMenuAnchor(event.currentTarget as HTMLElement)} color="secondary" endIcon={<Icon icon="Filter" />}>
          <Typography variant="caption">
            {countryLabel}
          </Typography>
        </Button>
      )}

      <Menu
        sx={{ padding: 0, maxHeight: 360, minWidth: 260, m: 0 }}
        anchorEl={countryMenuAnchor}
        open={Boolean(countryMenuAnchor)}
        onClose={() => setCountryMenuAnchor(null)}
      >
        {countryOptions.map((option) => {
          const selected = value.countries.includes(option.id);
          return (
            <MenuItem key={option.id} onClick={() => toggleCountry(option.id)} dense>
              <Checkbox checked={selected} size="small" sx={{ pointerEvents: 'none',  mr: 1 }} />
              <Stack minWidth={0}>
                <Typography variant="body2">{option.label}</Typography>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </Stack>
  );
};

export default SalesScopeFilters;
